import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Autocomplete,
  TextField,
  Typography,
  Box,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { LookupValueContext } from "../../common/contexts/LookupValueContext";
import {
  getHwioChannelOptions,
  getHwioChannelPrefix,
  getLookupInternalNames,
  isHwioTag,
  lookupNameToId,
} from "../helpers/Helpers";
import {
  getColumnSourceTypeRule,
  MODBUS_TCP_SOURCE_TYPE_NAMES,
} from "../helpers/SourceTypeColumns";

const BulkUpdateModal = ({
  open,
  onClose,
  selectedTags,
  selectedTagRecords = [],
  onBulkUpdate,
}) => {
  const lookupValues = useContext(LookupValueContext);
  const [selectedFieldInfo, setSelectedFieldInfo] = useState(null);
  const [fieldValue, setFieldValue] = useState("");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  // Get all available values for a lookup category
  const getLookupOptions = (category) =>
    getLookupInternalNames(category, lookupValues);

  const getLookupValueId = (internalName, category) => {
    return lookupNameToId(internalName, category, lookupValues);
  };

  /**
   * Resolves a field's source-type restriction, preferring the shared registry entry named by
   * `columnId` and falling back to types declared on the field itself.
   */
  const getFieldSourceTypeRule = (fieldInfo) => {
    if (!fieldInfo) {
      return undefined;
    }
    const rule = getColumnSourceTypeRule(fieldInfo.columnId);
    if (rule) {
      return rule;
    }
    return fieldInfo.sourceTypes?.length
      ? {
          sourceTypes: fieldInfo.sourceTypes,
          label: fieldInfo.sourceTypeLabel || fieldInfo.sourceTypes.join(", "),
        }
      : undefined;
  };

  /**
   * Builds the "this field only applies to some source types" warning for a field, or '' when the
   * field applies to every source type.
   */
  const buildSourceTypeWarning = (fieldInfo, prefix = "") => {
    const rule = getFieldSourceTypeRule(fieldInfo);
    if (!rule) {
      return "";
    }
    return `${prefix}This field is only compatible with ${rule.label} source types. Tags with other source types will be skipped.`;
  };

  /**
   * Channel labels for the Bulk Update list. The I/O type prefix is cosmetic — the value is parsed
   * prefix-agnostically on apply — so it is only shown when every selected HWIO tag agrees on it.
   */
  const getBulkChannelOptions = () => {
    const prefixes = new Set(
      selectedTagRecords
        .filter((tag) => isHwioTag(tag, lookupValues))
        .map((tag) => getHwioChannelPrefix(tag, lookupValues))
        .filter(Boolean)
    );
    return getHwioChannelOptions(prefixes.size === 1 ? [...prefixes][0] : "");
  };

  useEffect(() => {
    if (open) {
      setSelectedFieldInfo(null);
      setFieldValue("");
      setError("");
      setWarning("");
    }
  }, [open]);

  const handleApply = () => {
    if (!selectedFieldInfo) {
      setError("Please select a field to update");
      return;
    }

    if (fieldValue === "") {
      setError("Please enter a value");
      return;
    }

    const selectedIndices = Array.from(selectedTags);

    if (selectedIndices.length === 0) {
      setError("Please select at least one tag");
      return;
    }

    const applyWarning = buildSourceTypeWarning(selectedFieldInfo, "Note: ");
    if (applyWarning) {
      setWarning(applyWarning);
    }

    let valueToUpdate = fieldValue;
    if (selectedFieldInfo?.isLookupField) {
      const lookupValueId = getLookupValueId(
        fieldValue,
        selectedFieldInfo.lookupCategory
      );
      if (lookupValueId) {
        valueToUpdate = lookupValueId;
      } else {
        setError(`Invalid ${selectedFieldInfo.name} value`);
        return;
      }
    }

    onBulkUpdate(selectedIndices, selectedFieldInfo, valueToUpdate);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleFieldChange = (fieldInfo) => {
    setSelectedFieldInfo(fieldInfo);
    setFieldValue("");
    setError("");
    setWarning(buildSourceTypeWarning(fieldInfo));
  };

  const availableFields = [
    { key: "TagName", name: "Tag Name", type: "text" },
    { key: "TagIdentifier", name: "Tag Identifier", type: "text" },
    { key: "UniversalDescriptor", name: "Universal Descriptor", type: "text" },
    { key: "DisplayName", name: "Display Name", type: "text" },
    { key: "AssetNumber", name: "Asset Number", type: "text" },
    { key: "MaxValue", name: "Max Value", type: "text" },
    { key: "MinValue", name: "Min Value", type: "text" },
    { key: "Gain", name: "Gain", type: "text" },
    { key: "Offset", name: "Offset", type: "text" },
    { key: "AlarmLoLo", name: "Alarm Lo Lo", type: "text" },
    { key: "AlarmLo", name: "Alarm Lo", type: "text" },
    { key: "AlarmHi", name: "Alarm Hi", type: "text" },
    { key: "AlarmHiHi", name: "Alarm Hi Hi", type: "text" },
    { key: "DisplayFormat", name: "Display Format", type: "text" },
    { key: "UnitOfMeasure", name: "Unit of Measure", type: "text" },
    { key: "LoggingFrequency", name: "Logging Frequency", type: "text" },
    { key: "GroupName", name: "Group Name", type: "text" },
    { key: "IsActive", name: "Is Active", type: "bool" },
    { key: "Description", name: "Description", type: "text" },
    { key: "IsWriteable", name: "Is Writeable", type: "bool" },
    { key: "SyncToHub", name: "Sync To Hub", type: "bool" },
    { key: "Transform", name: "Transform", type: "text" },
    { key: "ScanFrequency", name: "Scan Frequency", type: "text" },
    { key: "Hidden", name: "Hidden", type: "bool" },
    { key: "SafetyShutdown", name: "Safety Shutdown", type: "bool" },
    {
      key: "TagSourceTypeId",
      name: "Source Type",
      isLookupField: true,
      lookupCategory: "TagSourceType",
    },
    {
      key: "AlarmTypeId",
      name: "Alarm Type",
      isLookupField: true,
      lookupCategory: "TagAlarmType",
    },
    {
      key: "DataTypeId",
      name: "Data Type",
      isLookupField: true,
      lookupCategory: "TagDataType",
    },
    {
      key: "AlarmSeverityId",
      name: "Alarm Severity",
      isLookupField: true,
      lookupCategory: "AlarmSeverity",
    },
    { key: "DisplayOrder", name: "Display Order", type: "text" },
    { key: "AlarmDeadband", name: "Alarm Deadband", type: "text" },
    { key: "AlarmTimeLimit", name: "Band Duration", type: "text" },
    { key: "AlarmArmingDelay", name: "Arming Delay", type: "text" },
    {
      key: "AlarmArmingTypeId",
      name: "Arming Type",
      isLookupField: true,
      lookupCategory: "AlarmArmingType",
    },
    { key: "ParentTagIdentifier", name: "Parent Tag", type: "text" },
    { key: "IsAlarmEnabled", name: "Is Alarm Enabled", type: "bool" },
    { key: "AlarmId", name: "Alarm ID", type: "alarmId" },
    { key: "Alarm2Id", name: "Alarm 2 ID", type: "alarm2Id" },
    {
      key: "AlarmLimitRangeId",
      name: "Alarm Limit Range",
      isLookupField: true,
      lookupCategory: "AlarmLimitRange",
    },
    // Source-type-specific fields. `columnId` points at the grid column whose compatibility rule
    // this field shares, so the source types live only in SourceTypeColumns.js.
    // Modbus fields stored in DeviceParams
    {
      key: "ModbusRegister",
      name: "Modbus Register",
      isDeviceParam: true,
      columnId: "ModbusRegister",
    },
    {
      key: "ModbusIP",
      name: "Modbus IP Address",
      isDeviceParam: true,
      columnId: "ModbusIP",
    },
    // The port is split in two here, unlike the grid: the Modbus Port column serves every Modbus type
    // and picks `port` or `port_number` per row, whereas each of these fields writes one key only. So
    // neither can borrow the Modbus Port column's rule — they declare their own narrower source types.
    {
      key: "ModbusPort",
      name: "Modbus Port (TCP)",
      isDeviceParam: true,
      sourceTypes: MODBUS_TCP_SOURCE_TYPE_NAMES,
    },
    {
      key: "ModbusPortRTU",
      name: "Modbus Port (RTU)",
      isDeviceParam: true,
      sourceTypes: ["ModbusRTU232", "ModbusRTU485"],
    },
    {
      key: "ModbusSlaveId",
      name: "Modbus Slave ID",
      isDeviceParam: true,
      columnId: "ModbusSlave",
    },
    {
      key: "LoSetpoint",
      name: "Lo Setpoint",
      isDeviceParam: true,
      columnId: "LoSetpoint",
    },
    {
      key: "LoLoSetpoint",
      name: "Lo Lo Setpoint",
      isDeviceParam: true,
      columnId: "LoLoSetpoint",
    },
    {
      key: "HiSetpoint",
      name: "Hi Setpoint",
      isDeviceParam: true,
      columnId: "HiSetpoint",
    },
    {
      key: "HiHiSetpoint",
      name: "Hi Hi Setpoint",
      isDeviceParam: true,
      columnId: "HiHiSetpoint",
    },
    {
      key: "SyncDirection",
      name: "Sync Direction",
      isDeviceParam: true,
      columnId: "SyncDirection",
      type: "select",
      options: ["None", "Controller to Hub", "Bi-directional"],
    },
    // HardWiredIO fields, stored directly on the tag record
    {
      key: "TagTypeId",
      name: "I/O Type (HWIO)",
      isLookupField: true,
      lookupCategory: "TagType",
      columnId: "HwioIOType",
    },
    {
      key: "ChannelNumber",
      name: "Channel Number (HWIO)",
      type: "hwioChannel",
      columnId: "HwioChannelNumber",
    },
    {
      key: "PortNumber",
      name: "Board Number (HWIO)",
      type: "text",
      columnId: "HwioBoardNumber",
    },
  ];

  const selectedCount = selectedTags.size;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth='sm' fullWidth>
      <DialogTitle>Bulk Update Selected Tags</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            {selectedCount} tag{selectedCount !== 1 ? "s" : ""} selected
          </Typography>
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {warning && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            {warning}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <Autocomplete
            options={availableFields}
            getOptionLabel={(option) => option.name}
            value={selectedFieldInfo}
            onChange={(_, newValue) => handleFieldChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Field to Update'
                placeholder='Search for a field...'
              />
            )}
            isOptionEqualToValue={(option, value) => option.key === value.key}
          />
        </FormControl>

        {selectedFieldInfo?.type === "bool" ? (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>New Value</InputLabel>
            <Select
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              label='New Value'>
              <MenuItem value='true'>True</MenuItem>
              <MenuItem value='false'>False</MenuItem>
            </Select>
          </FormControl>
        ) : selectedFieldInfo?.isLookupField ? (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>New Value</InputLabel>
            <Select
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              label='New Value'>
              {getLookupOptions(selectedFieldInfo.lookupCategory).map(
                (option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        ) : selectedFieldInfo?.type === "hwioChannel" ? (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              options={getBulkChannelOptions()}
              value={fieldValue || null}
              onChange={(_, newValue) => setFieldValue(newValue ?? "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='New Value'
                  placeholder='Search channels...'
                />
              )}
            />
          </FormControl>
        ) : selectedFieldInfo?.type === "alarmId" ? (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Special Options</InputLabel>
              <Select
                value={
                  fieldValue === "__TAG_IDENTIFIER__"
                    ? "__TAG_IDENTIFIER__"
                    : ""
                }
                onChange={(e) => setFieldValue(e.target.value)}
                label='Special Options'>
                <MenuItem value=''>None</MenuItem>
                <MenuItem value='__TAG_IDENTIFIER__'>
                  Use Tag Identifier (each Tag's AlarmID = its TagID)
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label='Alarm ID'
              value={fieldValue === "__TAG_IDENTIFIER__" ? "" : fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              placeholder='Enter Alarm ID'
              type='number'
              disabled={fieldValue === "__TAG_IDENTIFIER__"}
            />
          </>
        ) : selectedFieldInfo?.type === "alarm2Id" ? (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Special Options</InputLabel>
              <Select
                value={
                  fieldValue === "5+__TAG_IDENTIFIER__"
                    ? "5+__TAG_IDENTIFIER__"
                    : ""
                }
                onChange={(e) => setFieldValue(e.target.value)}
                label='Special Options'>
                <MenuItem value=''>None</MenuItem>
                <MenuItem value='5+__TAG_IDENTIFIER__'>
                  Use Tag Identifier (each Tag's Alarm2 ID = 5 in front of its
                  TagID)
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label='Alarm2 ID'
              value={fieldValue === "5+__TAG_IDENTIFIER__" ? "" : fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              placeholder='Enter Alarm2 ID'
              type='number'
              disabled={fieldValue === "5+__TAG_IDENTIFIER__"}
            />
          </>
        ) : selectedFieldInfo?.type === "select" ? (
          <>
            <Select
              onChange={(e) => setFieldValue(e.target.value)}
              value={fieldValue}
              fullWidth
              sx={{ mb: 2 }}>
              {selectedFieldInfo.options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </>
        ) : selectedFieldInfo?.key === "ParentTagIdentifier" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              fullWidth
              label='New Value'
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              placeholder='Enter the new value for the selected field'
            />
            <Tooltip title='Enter the Tag Identifier (a number) of the parent tag, not its Tag Name.'>
              <InfoOutlined
                titleAccess='Parent Tag help'
                fontSize='small'
                color='action'
              />
            </Tooltip>
          </Box>
        ) : (
          <TextField
            fullWidth
            label='New Value'
            value={fieldValue}
            onChange={(e) => setFieldValue(e.target.value)}
            placeholder='Enter the new value for the selected field'
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleApply} variant='contained'>
          Apply to {selectedCount} Tag{selectedCount !== 1 ? "s" : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUpdateModal;
